'use client';

/**
 * Error boundary for the (main) route segment.
 *
 * Catches unhandled render/data errors in any public page inside /(main)
 * (collections, items, profiles, circles, invite pages) and renders a
 * recovery UI instead of a blank screen.
 *
 * Identical shape to the (protected) boundary — kept separate so each
 * segment can evolve independently (e.g. different CTA for logged-out users).
 */

import { useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function MainError({ error, reset }: ErrorProps) {
  useEffect(() => {
    console.error('[MainError]', error);
  }, [error]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-sm w-full text-center space-y-5">
        <div className="flex justify-center">
          <div className="rounded-full bg-destructive/10 p-4">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
        </div>

        <div className="space-y-1.5">
          <h1 className="text-xl font-semibold">Something went wrong</h1>
          <p className="text-sm text-muted-foreground">
            This page ran into a problem. It's been logged and we'll look into it.
          </p>
          {process.env.NODE_ENV === 'development' && error.message && (
            <p className="text-xs font-mono text-muted-foreground bg-muted rounded p-2 mt-2 text-left break-all">
              {error.message}
            </p>
          )}
        </div>

        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={() => window.location.assign('/')}>
            Go home
          </Button>
          <Button onClick={reset}>Try again</Button>
        </div>
      </div>
    </div>
  );
}
